<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-slate-700">{{ id ? 'Editar Proprietário' : 'Novo Proprietário' }}</h1>
          <p class="text-sm text-slate-500">Preencha as informações do proprietário do veículo</p>
        </div>
        <button 
          @click="$router.push('/proprietario')" 
          class="text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1 text-sm font-medium"
        >
          <v-icon small>mdi-arrow-left</v-icon>
          Voltar para listagem
        </button>
      </div>

      <!-- FORMULÁRIO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-account-plus</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  {{ id ? 'EDITAR DADOS' : 'DADOS DO PROPRIETÁRIO' }}
              </h2>
          </div>

          <div class="p-8">
              <v-form ref="form" v-model="valid" lazy-validation class="space-y-6">
                  
                  <!-- Tipo de Cadastro -->
                  <div class="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div class="flex items-center gap-4">
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Tipo de Pessoa:</label>
                        <div class="flex items-center gap-6">
                            <label class="flex items-center gap-2 cursor-pointer group">
                                <input type="radio" :value="false" v-model="data.type" class="w-4 h-4 text-orange-500 border-slate-300 focus:ring-orange-500">
                                <span class="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">Pessoa Física</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer group">
                                <input type="radio" :value="true" v-model="data.type" class="w-4 h-4 text-orange-500 border-slate-300 focus:ring-orange-500">
                                <span class="text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">Pessoa Jurídica</span>
                            </label>
                        </div>
                      </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                      <!-- Nome/Razão Social -->
                      <div class="md:col-span-8">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                             {{ data.type ? 'Razão Social' : 'Nome Completo' }}
                          </label>
                          <v-text-field
                            v-model="data.name"
                            :rules="fieldRules"
                            placeholder="Ex: Nome do Proprietário..."
                            outlined
                            dense
                            hide-details="auto"
                            class="custom-input"
                          />
                      </div>

                      <!-- CPF/CNPJ -->
                      <div class="md:col-span-4">
                          <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                             {{ data.type ? 'CNPJ' : 'CPF' }}
                          </label>
                          <v-text-field
                            v-model="data.document"
                            :maxlength="data.type ? 18 : 14"
                            @input="data.type ? setCnpj(data.document) : setCpf(data.document)"
                            :error="data.type ? !validCnpj(data.document) : !validCpf(data.document)"
                            :error-messages="data.type ? (!validCnpj(data.document) && data.document ? 'CNPJ INVÁLIDO' : '') : (!validCpf(data.document) && data.document ? 'CPF INVÁLIDO' : '')"
                            outlined
                            dense
                            hide-details="auto"
                            class="custom-input font-mono"
                          />
                      </div>
                  </div>

                  <!-- Ações -->
                  <div class="flex justify-end gap-3 pt-6 border-t border-slate-100">
                      <button 
                        @click="$router.push('/proprietario')" 
                        type="button"
                        class="text-slate-500 hover:text-slate-700 font-bold text-[10px] uppercase px-6 py-2 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button 
                        @click="validate"
                        :disabled="!valid || loader"
                        type="button"
                        class="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold text-[10px] px-10 py-2.5 rounded shadow hover:shadow-md transform hover:-translate-y-0.5 transition-all uppercase tracking-widest inline-flex items-center justify-center gap-2 w-auto max-w-max"
                      >
                        <v-icon small color="white" style="font-size: 14px;">{{ loader ? 'mdi-loading' : (id ? 'mdi-pencil' : 'mdi-check') }}</v-icon>
                        {{ loader ? 'PROCESSANDO...' : (id ? 'ATUALIZAR' : 'CADASTRAR') }}
                      </button>
                  </div>
              </v-form>
          </div>
      </section>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      multi-line
      bottom
      right
      :color="snack.color"
      v-model="snack.show"
      transition="slide-x-reverse-transition"
      class="mb-6 mr-6"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="font-medium">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      id: null,
      name: "",
      document: "",
      type: null,
    },
  }),
  created() {
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/proprietario/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    setCnpj(v) {
      v = v.replace(/\D/g, "");
      v = v.replace(/^(\d{2})(\d)/, "$1.$2");
      v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
      v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
      v = v.replace(/(\d{4})(\d)/, "$1-$2");
      this.data.document = v;
    },
    setCpf(v) {
      v = v.replace(/\D/g, "");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      this.data.document = v;
    },
    validCnpj(v) {
      let cnpj = v.replace(/\D/gim, "");

      // Valida a quantidade de caracteres
      if (cnpj.length !== 14) return false;

      // Elimina inválidos com todos os caracteres iguais
      if (/^(\d)\1+$/.test(cnpj)) return false;

      // Cáculo de validação
      let t = cnpj.length - 2,
        d = cnpj.substring(t),
        d1 = parseInt(d.charAt(0)),
        d2 = parseInt(d.charAt(1)),
        calc = (x) => {
          let n = cnpj.substring(0, x),
            y = x - 7,
            s = 0,
            r = 0;

          for (let i = x; i >= 1; i--) {
            s += n.charAt(x - i) * y--;
            if (y < 2) y = 9;
          }

          r = 11 - (s % 11);
          return r > 9 ? 0 : r;
        };
      return calc(t) === d1 && calc(t + 1) === d2;
    },
    validCpf(cpf) {
      cpf = cpf.replace(/\D/g, "");
      if (cpf.toString().length != 11 || /^(\d)\1{10}$/.test(cpf)) return false;
      var result = true;
      [9, 10].forEach(function (j) {
        var soma = 0,
          r;
        cpf
          .split(/(?=)/)
          .splice(0, j)
          .forEach(function (e, i) {
            soma += parseInt(e) * (j + 2 - (i + 1));
          });
        r = soma % 11;
        r = r < 2 ? 0 : 11 - r;
        if (r != cpf.substring(j, j + 1)) result = false;
      });
      return result;
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/proprietario/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/proprietario");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/proprietario", this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            setTimeout(() => {
              this.$router.push("/proprietario");
            }, 1500);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.w-full { width: 100%; }
.p-4 { padding: 1rem; }

.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
